/**
 * Privacy-focused Analytics
 * Uses PostHog for anonymous usage tracking
 */

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
}

class Analytics {
  private enabled: boolean = false;
  private posthog: any = null;

  /**
   * Initialize analytics
   * Only loads if user hasn't opted out
   */
  init(apiKey?: string, host?: string) {
    // Check if user opted out
    const hasOptedOut = localStorage.getItem('analytics-opted-out') === 'true';
    if (hasOptedOut) {
      console.log('[Analytics] User opted out, not loading');
      return;
    }

    // Don't load in development
    if (import.meta.env.DEV) {
      console.log('[Analytics] Skipping in development');
      return;
    }

    // Load PostHog if API key provided
    if (apiKey && typeof window !== 'undefined') {
      this.loadPostHog(apiKey, host);
    }
  }

  /**
   * Dynamically load PostHog SDK
   */
  private async loadPostHog(apiKey: string, host?: string) {
    try {
      const { default: posthog } = await import('posthog-js');

      this.posthog = posthog;

      posthog.init(apiKey, {
        api_host: host || 'https://app.posthog.com',
        autocapture: false, // Privacy: Only track what we explicitly send
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: true, // Privacy: No session recordings
        disable_surveys: true, // Privacy: No surveys
        loaded: (ph) => {
          this.enabled = true;
          console.log('[Analytics] PostHog loaded and ready');
        },
      });

      // Set enabled immediately after init
      this.enabled = true;
      console.log('[Analytics] PostHog initialized with key:', apiKey.substring(0, 10) + '...');
    } catch (error) {
      console.error('[Analytics] Failed to load PostHog:', error);
    }
  }

  /**
   * Track custom event
   */
  track(event: string, properties?: Record<string, unknown>) {
    if (!this.enabled || !this.posthog) {
      console.warn('[Analytics] Cannot track event - PostHog not loaded:', event);
      return;
    }

    console.log('[Analytics] Tracking event:', event, properties);
    this.posthog.capture(event, properties);
  }

  /**
   * Track page view
   */
  pageView(path?: string) {
    if (!this.enabled || !this.posthog) {
      console.warn('[Analytics] Cannot track pageview - PostHog not loaded');
      return;
    }

    console.log('[Analytics] Tracking pageview:', path || window.location.href);
    this.posthog.capture('$pageview', {
      $current_url: path || window.location.href,
    });
  }

  /**
   * Opt out of analytics
   */
  optOut() {
    localStorage.setItem('analytics-opted-out', 'true');
    this.enabled = false;

    if (this.posthog) {
      this.posthog.opt_out_capturing();
    }

    console.log('[Analytics] User opted out');
  }

  /**
   * Opt in to analytics
   */
  optIn() {
    localStorage.removeItem('analytics-opted-out');

    if (this.posthog) {
      this.posthog.opt_in_capturing();
      this.enabled = true;
    }

    console.log('[Analytics] User opted in');
  }

  /**
   * Check if user has opted out
   */
  hasOptedOut(): boolean {
    return localStorage.getItem('analytics-opted-out') === 'true';
  }
}

// Singleton instance
export const analytics = new Analytics();

// Analytics events
export const AnalyticsEvents = {
  // File uploads
  FILE_UPLOADED: 'file_uploaded',
  SAMPLE_LOADED: 'sample_loaded',

  // Model usage
  MODEL_RUN: 'model_run',
  MODEL_SUCCESS: 'model_success',
  MODEL_ERROR: 'model_error',

  // Exports
  EXPORT_CSV: 'export_csv',
  EXPORT_PNG: 'export_png',
  COPY_CLIPBOARD: 'copy_clipboard',

  // UI interactions
  WELCOME_COMPLETED: 'welcome_completed',
  WELCOME_SKIPPED: 'welcome_skipped',
  SAMPLE_CLICKED: 'sample_clicked',

  // Performance
  WASM_LOAD_TIME: 'wasm_load_time',
  FORECAST_TIME: 'forecast_time',
} as const;

/**
 * Track model run
 */
export function trackModelRun(model: string, params: Record<string, unknown>) {
  analytics.track(AnalyticsEvents.MODEL_RUN, {
    model,
    ...params,
  });
}

/**
 * Track file upload
 */
export function trackFileUpload(fileType: string, rows: number, sizeMB: number) {
  analytics.track(AnalyticsEvents.FILE_UPLOADED, {
    file_type: fileType,
    rows,
    size_mb: sizeMB,
  });
}

/**
 * Track export
 */
export function trackExport(format: string) {
  analytics.track(
    format === 'csv'
      ? AnalyticsEvents.EXPORT_CSV
      : format === 'png'
        ? AnalyticsEvents.EXPORT_PNG
        : AnalyticsEvents.COPY_CLIPBOARD
  );
}

/**
 * Track performance metric
 */
export function trackPerformance(metric: string, duration: number) {
  analytics.track(metric, {
    duration_ms: duration,
  });
}
